import Phaser from "phaser";
import { getLeaderboard, submitScore } from "./api";
import { FlightSnapshot } from "./game/flightModel";
import { SnappyScene } from "./game/SnappyScene";
import "./styles.css";

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

const scoreEl = document.querySelector<HTMLElement>("#score")!;
const bestEl = document.querySelector<HTMLElement>("#best-score")!;
const overlay = document.querySelector<HTMLElement>("#overlay")!;
const overlayCopy = document.querySelector<HTMLElement>("#overlay-copy")!;
const startButton = document.querySelector<HTMLButtonElement>("#start-button")!;
const muteButton = document.querySelector<HTMLButtonElement>("#mute-button")!;
const form = document.querySelector<HTMLFormElement>("#score-form")!;
const nameInput = document.querySelector<HTMLInputElement>("#player-name")!;
const submitButton = document.querySelector<HTMLButtonElement>("#submit-score")!;
const submitStatus = document.querySelector<HTMLElement>("#submit-status")!;
const leaderboardEl = document.querySelector<HTMLOListElement>("#leaderboard")!;

nameInput.value = localStorage.getItem("snappybird:name") ?? "";

new Phaser.Game({
  type: Phaser.AUTO,
  parent: "game",
  backgroundColor: "#7bd8f7",
  width: 480,
  height: 720,
  physics: {
    default: "arcade",
    arcade: {
      debug: false
    }
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: 480,
    height: 720
  },
  scene: [SnappyScene]
});

startButton.addEventListener("click", () => {
  window.dispatchEvent(new CustomEvent("snappybird:start"));
});

muteButton.addEventListener("click", () => {
  const nextState = muteButton.getAttribute("aria-pressed") !== "true";
  muteButton.setAttribute("aria-pressed", String(nextState));
  muteButton.textContent = nextState ? "Muted" : "Sound";
  window.dispatchEvent(new CustomEvent("snappybird:toggle-mute"));
});

form.addEventListener("submit", async (event) => {
  event.preventDefault();
  const name = nameInput.value.trim() || "Pilot";
  const score = Number(scoreEl.textContent ?? 0);
  localStorage.setItem("snappybird:name", name);

  submitButton.disabled = true;
  submitStatus.textContent = "Submitting...";
  try {
    const entries = await submitScore(name, score);
    renderLeaderboard(entries);
    submitStatus.textContent = "Score posted.";
    window.dispatchEvent(new CustomEvent("snappybird:submitted"));
  } catch {
    submitStatus.textContent = "Could not post score.";
  } finally {
    submitButton.disabled = false;
  }
});

window.addEventListener("snappybird:state", (event) => {
  const snapshot = (event as CustomEvent<FlightSnapshot>).detail;
  renderState(snapshot);
});

void refreshLeaderboard();

async function refreshLeaderboard() {
  renderLeaderboard(await getLeaderboard());
}

function renderState(snapshot: FlightSnapshot) {
  scoreEl.textContent = String(snapshot.score);
  bestEl.textContent = String(snapshot.best);
  submitButton.disabled = !snapshot.canSubmit;
  form.classList.toggle("is-active", snapshot.canSubmit);

  if (snapshot.status === "running") {
    overlay.classList.remove("is-visible");
    return;
  }

  overlay.classList.add("is-visible");

  if (snapshot.status === "ready") {
    startButton.textContent = "Start";
    overlayCopy.textContent = "Tap, click, or press Space to flap. Thread the pipes and chase the daily board.";
    submitStatus.textContent = "";
  } else if (snapshot.status === "paused") {
    startButton.textContent = "Resume";
    overlayCopy.textContent = "Paused. Press Esc or tap Start to keep flying.";
  } else {
    startButton.textContent = "Retry";
    overlayCopy.textContent =
      snapshot.score > 0
        ? `Run finished at ${snapshot.score}. Submit the score or jump straight back in.`
        : "Run finished at 0. Jump straight back in and clear the first gate.";
    if (!snapshot.canSubmit && snapshot.score > 0) {
      submitStatus.textContent = "Score posted.";
    }
  }
}

function renderLeaderboard(entries: Awaited<ReturnType<typeof getLeaderboard>>) {
  leaderboardEl.replaceChildren();

  if (entries.length === 0) {
    const empty = document.createElement("li");
    empty.className = "leaderboard-empty";
    empty.textContent = "No scores yet";
    leaderboardEl.append(empty);
    return;
  }

  for (const entry of entries) {
    const item = document.createElement("li");
    const name = document.createElement("span");
    const score = document.createElement("strong");
    name.textContent = entry.name;
    score.textContent = String(entry.score);
    item.append(name, score);
    leaderboardEl.append(item);
  }
}
